/**
 * Hook quản lý danh sách admin và thao tác CRUD tài khoản admin.
 *
 * Chức năng:
 * - Tải danh sách admin khi mount.
 * - Tạo admin mới (validate SĐT, chuẩn hóa phone).
 * - Reset mật khẩu admin (tối thiểu 8 ký tự).
 */
import { useEffect, useState } from 'react';
import { UI_MESSAGES } from '../../../../shared/constants/messages';
import { TEXT } from '../../../../shared/languages';
import { getApiErrorMessage } from '../../../../shared/utils/apiErrors';
import {
  getPhoneValidationError,
  normalizeVietnamesePhone,
} from '../../../../shared/utils/vietnamesePhone';
import { adminUserApi } from '../api/adminUserApi';

/** Form tạo admin rỗng mặc định */
const emptyAdminForm = {
  name: '',
  email: '',
  phone: '',
  password: '',
};

/**
 * Hook quản lý state trang AdminUsersPage.
 * @returns {Object} Danh sách admin, form, trạng thái loading và handlers
 */
export const useAdminUsers = () => {
  const [adminList, setAdminList] = useState([]);
  const [adminForm, setAdminForm] = useState(emptyAdminForm);
  const [resetPasswordByAdminId, setResetPasswordByAdminId] = useState({});
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(true);
  const [isCreatingAdmin, setIsCreatingAdmin] = useState(false);
  const [resettingAdminId, setResettingAdminId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const users = TEXT.admin.users;

  const loadAdminUsers = async () => {
    setIsLoadingAdmins(true);

    try {
      const response = await adminUserApi.getAdminUsers();
      setAdminList(response.data.data);
    } catch (requestError) {
      setErrorMessage(getApiErrorMessage(requestError, UI_MESSAGES.adminUsersLoadError));
    } finally {
      setIsLoadingAdmins(false);
    }
  };

  useEffect(() => {
    loadAdminUsers();
  }, []);

  const updateAdminFormField = (fieldName, fieldValue) => {
    if (fieldName === 'phone') {
      setPhoneError('');
    }

    setAdminForm((currentAdminForm) => ({
      ...currentAdminForm,
      [fieldName]: fieldValue,
    }));
  };

  const updateResetPassword = (adminId, password) => {
    setResetPasswordByAdminId((currentPasswords) => ({
      ...currentPasswords,
      [adminId]: password,
    }));
  };

  const createAdmin = async () => {
    setIsCreatingAdmin(true);
    setErrorMessage('');
    setPhoneError('');
    setSuccessMessage('');

    const phoneValidationKey = getPhoneValidationError(adminForm.phone);

    if (phoneValidationKey) {
      setPhoneError(TEXT.validation[phoneValidationKey]);
      setIsCreatingAdmin(false);
      return;
    }

    try {
      const response = await adminUserApi.createAdminUser({
        ...adminForm,
        phone: normalizeVietnamesePhone(adminForm.phone) || null,
      });
      setAdminList((currentAdminList) => [response.data.data, ...currentAdminList]);
      setAdminForm(emptyAdminForm);
      setSuccessMessage(users.createSuccess);
    } catch (requestError) {
      setErrorMessage(getApiErrorMessage(requestError, UI_MESSAGES.adminUserCreateError));
    } finally {
      setIsCreatingAdmin(false);
    }
  };

  const resetAdminPassword = async (adminId) => {
    const newPassword = resetPasswordByAdminId[adminId] || '';

    if (newPassword.length < 8) {
      setErrorMessage(users.passwordMinLength);
      return;
    }

    setResettingAdminId(adminId);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await adminUserApi.resetAdminPassword(adminId, newPassword);
      updateResetPassword(adminId, '');
      setSuccessMessage(users.resetSuccess);
    } catch (requestError) {
      setErrorMessage(getApiErrorMessage(requestError, UI_MESSAGES.adminPasswordResetError));
    } finally {
      setResettingAdminId(null);
    }
  };

  return {
    adminList,
    adminForm,
    resetPasswordByAdminId,
    isLoadingAdmins,
    isCreatingAdmin,
    resettingAdminId,
    errorMessage,
    phoneError,
    successMessage,
    updateAdminFormField,
    updateResetPassword,
    createAdmin,
    resetAdminPassword,
  };
};
